import { Fragment, FunctionComponent } from "react"
import { useTranslation } from "react-i18next"
import { statusMap } from "@repo/core/models"
import { format } from "date-fns"
import { CalendarClock } from "lucide-react"

import Icon from "@/components/Icon"
import { EventStatusPill } from "@/components/status-pill"
import { formatDateTime } from "@/helpers/dateFormat"
import { formatStringValue as formatValue } from "@/helpers/valueFormat"
import { useFormValidation } from "@/hooks/formValidation"
import * as connectPanelValue from "@/pages/CustomConnectPanel/value"

const styles = {
  container:
    "bg-neutral-100 border border-neutral-300 rounded px-4 py-2 shadow-card flex flex-col gap-y-2 font-body-medium-regular text-neutral-700",
  reason: "px-4 py-2 rounded border border-neutral-300 my-1",
  footerContainer: "flex gap-x-2 items-center my-1 flex-wrap",
  textWrapper: "flex gap-x-1 items-center",
  textContent: "font-body-medium-bold",
}

interface Props {
  event: connectPanelValue.ActivityFeedEntry.ActivityFeedEntryType
}

const HistoryItemCard: FunctionComponent<Props> = ({ event }) => {
  const { t } = useTranslation()
  const { isKanji } = useFormValidation()

  const firstName = event.status_event?.first_name ?? ""
  const lastName = event.status_event?.last_name ?? ""

  const agentName = isKanji(lastName || firstName)
    ? `${lastName}${firstName}`
    : `${firstName} ${lastName}`

  return (
    <div className={styles.container}>
      <span data-unmask>{formatDateTime(event.status_event?.created_at)}</span>
      <EventMeta event={event} />
      {event.comment?.comment && (
        <div className={styles.reason} data-unmask>
          {formatValue(event.comment?.comment)
            .split("\n")
            .map((line, index) => (
              <Fragment key={index}>
                {line}
                <br />
              </Fragment>
            ))}
        </div>
      )}
      <div className={styles.footerContainer}>
        <div className={styles.textWrapper}>
          <span data-unmask>{t("caseHistory.agent")}:</span>
          <span className={styles.textContent}>
            {formatValue(agentName.trim())}
          </span>
        </div>
        <Icon name="dot" size="xxsm" className={"text-neutral-500"} />
        <div className={styles.textWrapper}>
          <span data-unmask>{t("caseHistory.campaign")}:</span>
          <span className={styles.textContent} data-unmask>
            {formatValue(event.campaign_id)}
          </span>
        </div>
      </div>
    </div>
  )
}

function EventMeta({ event }: Props) {
  if (!event.status_event && !event.task_contact) {
    return null
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {event.status_event && (
        <EventStatusPill value={event.status_event.event_name} />
      )}
      {event.task_contact?.reason === statusMap.CallLaterAtGivenTime &&
        event.task_contact?.scheduled_at && (
          <div className="flex items-center gap-x-1">
            <CalendarClock className="size-5 text-neutral-500" />
            <span className="font-body-medium-regular text-neutral-600">
              {format(event.task_contact.scheduled_at, "yyyy/MM/dd HH:mm")}
            </span>
          </div>
        )}
    </div>
  )
}

export default HistoryItemCard
